import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Briefcase, Users, Plus, X, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { INDUSTRY_NAMES } from "../lib/industries";

const EMPTY = {
  title: "", company_name: "", location: "", industry: INDUSTRY_NAMES[0], department: "",
  employment_type: "full_time", experience_min: 0, experience_max: 5,
  salary_min: 0, salary_max: 0, qualification: "", description: "",
  responsibilities: [], requirements: [], skills: [], is_active: true,
};

export default function EmployerDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [modal, setModal] = useState(false);
  const [job, setJob] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const [j, a] = await Promise.all([api.get("/my/jobs"), api.get("/applications")]);
    setJobs(j.data);
    setApps(a.data);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...job,
        responsibilities: typeof job.responsibilities === "string" ? job.responsibilities.split("\n").filter(Boolean) : job.responsibilities,
        requirements: typeof job.requirements === "string" ? job.requirements.split("\n").filter(Boolean) : job.requirements,
        skills: typeof job.skills === "string" ? job.skills.split(",").map(s => s.trim()).filter(Boolean) : job.skills,
        experience_min: parseFloat(job.experience_min), experience_max: parseFloat(job.experience_max),
        salary_min: parseFloat(job.salary_min), salary_max: parseFloat(job.salary_max),
      };
      await api.post("/jobs", payload);
      toast.success("Job posted");
      setModal(false); setJob(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="employer-dashboard">
      <section className="bg-[#0F2A4A] text-white py-14">
        <div className="ae-container flex justify-between items-start gap-4 flex-wrap">
          <div>
            <div className="ae-overline mb-2">Employer Portal</div>
            <h1 className="text-3xl md:text-4xl font-light">{user.company_name || user.full_name}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setModal(true)} className="ae-btn-primary flex items-center gap-2 text-sm" data-testid="post-job-btn">
              <Plus className="w-4 h-4" /> Post a Job
            </button>
            <button onClick={() => { logout(); nav("/"); }} className="text-white/70 hover:text-white text-sm flex items-center gap-2" data-testid="emp-logout">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      </section>

      <div className="ae-container py-8">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <Stat icon={Briefcase} label="Active Jobs" value={jobs.filter(j => j.is_active).length} />
          <Stat icon={Users} label="Applications" value={apps.length} />
          <Stat icon={Users} label="Shortlisted" value={apps.filter(a => a.status === "shortlisted").length} />
        </div>

        <div className="flex gap-2 mb-6 border-b border-slate-200">
          {[{ k: "jobs", l: "My Jobs" }, { k: "apps", l: "Applications" }].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`emp-tab-${t.k}`}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-all ${tab === t.k ? "border-[#C5A059] text-[#0F2A4A]" : "border-transparent text-slate-500"}`}>
              {t.l}
            </button>
          ))}
        </div>

        {tab === "jobs" && (
          <div className="space-y-3">
            {jobs.length === 0 && <div className="ae-card p-12 text-center text-slate-500">No jobs yet. Post your first job!</div>}
            {jobs.map(j => (
              <div key={j.id} className="ae-card p-6 flex justify-between items-center" data-testid={`emp-job-${j.id}`}>
                <div>
                  <h3 className="font-heading font-medium text-[#0F2A4A]">{j.title}</h3>
                  <div className="text-sm text-slate-500 mt-1">{j.location} · {j.applications_count || 0} applications</div>
                </div>
                <span className={`text-xs px-3 py-1.5 rounded-full font-medium ${j.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {j.is_active ? "Active" : "Closed"}
                </span>
              </div>
            ))}
          </div>
        )}

        {tab === "apps" && (
          <div className="space-y-3">
            {apps.length === 0 && <div className="ae-card p-12 text-center text-slate-500">No applications yet.</div>}
            {apps.map(a => {
              const token = localStorage.getItem("ae_token");
              const resumeUrl = a.resume_path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${a.resume_path}?auth=${token}` : null;
              return (
                <div key={a.id} className="ae-card p-6 flex justify-between items-center gap-4 flex-wrap" data-testid={`emp-app-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-medium text-[#0F2A4A]">{a.candidate_name}</h3>
                    <div className="text-sm text-slate-500 mt-1">{a.job_title} · {a.candidate_email}</div>
                    {a.candidate_phone && <div className="text-xs text-slate-400 mt-0.5">📱 {a.candidate_phone}</div>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {resumeUrl ? (
                      <a href={resumeUrl} target="_blank" rel="noreferrer"
                        data-testid={`emp-resume-${a.id}`}
                        className="text-xs px-3 py-1.5 rounded-lg bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-1.5">
                        📄 Download Resume
                      </a>
                    ) : (
                      <span className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-400 inline-flex items-center gap-1.5">📄 No resume</span>
                    )}
                    <span className="text-xs px-3 py-1.5 rounded-full font-medium bg-slate-100 text-slate-700 capitalize">{a.status.replace("_", " ")}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setModal(false)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100 flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="font-heading font-medium text-xl text-[#0F2A4A]">Post a New Job</h2>
              <button onClick={() => setModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <FieldIn label="Job Title" val={job.title} onChange={v => setJob({ ...job, title: v })} tid="j-title" />
                <FieldIn label="Company Name" val={job.company_name} onChange={v => setJob({ ...job, company_name: v })} tid="j-company" />
                <FieldIn label="Location" val={job.location} onChange={v => setJob({ ...job, location: v })} tid="j-loc" />
                <FieldSel label="Industry" val={job.industry} onChange={v => setJob({ ...job, industry: v })} tid="j-industry"
                  opts={INDUSTRY_NAMES} />
                <FieldIn label="Department" val={job.department} onChange={v => setJob({ ...job, department: v })} tid="j-dept" />
                <FieldSel label="Employment Type" val={job.employment_type} onChange={v => setJob({ ...job, employment_type: v })} tid="j-type"
                  opts={[{v:"full_time",l:"Full-time"},{v:"contract",l:"Contract"},{v:"part_time",l:"Part-time"},{v:"internship",l:"Internship"}]} />
                <FieldIn label="Min Experience (yrs)" type="number" val={job.experience_min} onChange={v => setJob({ ...job, experience_min: v })} tid="j-emin" />
                <FieldIn label="Max Experience (yrs)" type="number" val={job.experience_max} onChange={v => setJob({ ...job, experience_max: v })} tid="j-emax" />
                <FieldIn label="Salary Min (₹ p.a.)" type="number" val={job.salary_min} onChange={v => setJob({ ...job, salary_min: v })} tid="j-smin" />
                <FieldIn label="Salary Max (₹ p.a.)" type="number" val={job.salary_max} onChange={v => setJob({ ...job, salary_max: v })} tid="j-smax" />
              </div>
              <FieldIn label="Qualification" val={job.qualification} onChange={v => setJob({ ...job, qualification: v })} tid="j-qual" />
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Description</label>
                <textarea rows={4} value={job.description} onChange={e => setJob({ ...job, description: e.target.value })}
                  data-testid="j-desc" className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] outline-none px-4 py-3 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Responsibilities (one per line)</label>
                <textarea rows={4} value={Array.isArray(job.responsibilities) ? job.responsibilities.join("\n") : job.responsibilities}
                  onChange={e => setJob({ ...job, responsibilities: e.target.value })}
                  data-testid="j-resp" className="w-full rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-[#C5A059] px-4 py-3 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Requirements (one per line)</label>
                <textarea rows={4} value={Array.isArray(job.requirements) ? job.requirements.join("\n") : job.requirements}
                  onChange={e => setJob({ ...job, requirements: e.target.value })}
                  data-testid="j-req" className="w-full rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-[#C5A059] px-4 py-3 text-sm" />
              </div>
              <FieldIn label="Skills (comma-separated)" val={Array.isArray(job.skills) ? job.skills.join(", ") : job.skills} onChange={v => setJob({ ...job, skills: v })} tid="j-skills" />
              <div className="flex gap-2 pt-4">
                <button onClick={() => setModal(false)} className="ae-btn-outline text-sm flex-1">Cancel</button>
                <button onClick={save} disabled={saving} className="ae-btn-primary text-sm flex-1" data-testid="j-submit">
                  {saving ? "Posting…" : "Post Job"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="ae-card p-6">
    <Icon className="w-6 h-6 text-[#C5A059] mb-2" />
    <div className="text-3xl font-heading font-semibold text-[#0F2A4A]">{value}</div>
    <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
  </div>
);
const FieldIn = ({ label, val, onChange, type = "text", tid }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">{label}</label>
    <input type={type} value={val ?? ""} onChange={e => onChange(e.target.value)}
      data-testid={tid} className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] outline-none px-4 py-3 text-sm" />
  </div>
);
const FieldSel = ({ label, val, onChange, opts, tid }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">{label}</label>
    <select value={val} onChange={e => onChange(e.target.value)} data-testid={tid}
      className="w-full rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-[#C5A059] px-4 py-3 text-sm">
      {opts.map(o => typeof o === "string" ? <option key={o}>{o}</option> : <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </div>
);
